import { Trans, useLingui } from "@lingui/react/macro";
import { dischargePatient, useParentRoutesPatient } from "@/api/Patients";
import { FilledButton, OutlinedButton } from "@medoma/components/Button/Button";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import Form from "@/components/Form/Form";
import { useForm, FormProvider } from "react-hook-form";
import { useEffect, useState } from "react";
import Checkbox from "@/components/Checkbox/Checkbox";
import styles from "./Discharge.module.scss";
import type {
  IAdmittedPatient,
  IExistingPatient,
  IPatient,
} from "@medoma/models/patients";
import {
  getPatientFullName,
  patientStatusSchema,
  patientTypeSchema,
} from "@medoma/models/patients";
import { finishedInvoicingCodesForPatientOptions } from "@/api/Invoicing";
import { useUserProfile } from "@/api/Users";
import {
  organizationSchema,
  type IOrganization,
} from "@medoma/models/organizations";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import RadioButton from "@medoma/components/RadioButton/RadioButton";
import InputField from "@medoma/components/InputField/InputField";
import RadioButtons from "@medoma/components/RadioButton/RadioButtons";
import {
  calendarDateSchema,
  machineFormat,
} from "@medoma/models/date-and-time";
import type { IActivity } from "@/api/Activities";
import { patientActiveActivitiesExceptForOneTimeActivitiesBeforeTodayQueryOptions } from "@/api/Activities";
import { ActivitiesToCloseOrReactivate } from "../ActivitiesToCloseOrReactivate";
import { useNavigate } from "react-router";
import { Text } from "@medoma/components/Text/Text";
import { startOfToday, subDays } from "date-fns";
import { DischargeReview } from "./DischargeReview";
import { CareEpisodeSubpage } from "../CareEpisodeSubpage";
import subpageStyles from "../CareEpisodeSubpage.module.scss";

export const DischargeSection = () => {
  const { t } = useLingui();
  const patient = useParentRoutesPatient();

  return (
    <CareEpisodeSubpage title={t`Skriv ut`}>
      <div className={subpageStyles.subPageContainer}>
        {patient.status === patientStatusSchema.enum.admitted ? (
          <Discharge patient={patient} />
        ) : (
          <NotDischargeable patient={patient} />
        )}
      </div>
    </CareEpisodeSubpage>
  );
};

type IDischargeFormData = {
  cancelledScheduledActivities: boolean;
  dischargeDate: string;
  timeOfDischarge: "today" | "earlier";
  activityIds: string[];
};

const isDischarged = ({ status }: Pick<IPatient, "status">) =>
  status === patientStatusSchema.enum.discharged;

const NotDischargeable = ({ patient }: { patient: IExistingPatient }) => {
  const { t } = useLingui();
  const patientName = getPatientFullName(patient.name);

  if (isDischarged(patient)) {
    return (
      <div className={styles.dischargedWrapper}>
        <p>{t`${patientName} är utskriven`}</p>
      </div>
    );
  }

  return (
    <p className={styles.dischargedWrapper}>
      <Trans>Endast inskrivna patienter kan skrivas ut</Trans>
    </p>
  );
};

/* oxlint-disable lingui/no-unlocalized-strings */
// Don't translate strings corresponding to user submitted data / templates
// Source: https://linear.app/medoma/issue/MED-5154/check-if-all-mandatory-codes-are-reported-when-discharging-patient#comment-4a1d8828
const EXPECTED_CODES_WHEN_DISCHARGING_AHC_PATIENT = [
  {
    code: "XS007",
    name: "Konferens med patient",
  },
  {
    code: "XV015",
    name: "Läkemedelsgenomgång, enkel",
  },
  {
    code: "UV017",
    name: "Trygghetskvitto",
  },
];
/* oxlint-enable lingui/no-unlocalized-strings */

const Discharge = ({ patient }: { patient: IAdmittedPatient }) => {
  const { t } = useLingui();
  const { organization } = useUserProfile();
  const {
    data: activities,
    isPending,
    isFetchedAfterMount,
    isError,
  } = useQuery(
    patientActiveActivitiesExceptForOneTimeActivitiesBeforeTodayQueryOptions({
      patientId: patient.id,
    }),
  );

  // The form checks every listed activity once, when it mounts, so cached
  // activities could leave out ones added since they were fetched
  if (isPending || !isFetchedAfterMount) {
    return <Loading message={t`Hämtar information för utskrivning`} />;
  }

  return (
    <DischargeForm
      patient={patient}
      organization={organization}
      activities={activities ?? []}
      errorFetchingActivities={isError}
    />
  );
};

const organizationRequiresInvoicingCodesBeforeDischarge = (
  organization: IOrganization,
) => {
  switch (organization) {
    case organizationSchema.enum.capioahc:
    case organizationSchema.enum.slso:
    case organizationSchema.enum.ssh:
      return true;
    case organizationSchema.enum.sus:
    case organizationSchema.enum.hahag:
    case organizationSchema.enum.ksgr:
    case organizationSchema.enum.rakus:
    case organizationSchema.enum.medoma:
    case organizationSchema.enum.qa:
      return false;
    default:
      throw organization satisfies never;
  }
};

export const DischargeForm = ({
  patient,
  organization,
  activities,
  errorFetchingActivities,
}: {
  patient: IAdmittedPatient;
  organization: IOrganization;
  activities: IActivity[];
  errorFetchingActivities: boolean;
}) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const shouldCheckForRequiredInvoicingCodes =
    patient.patientType === patientTypeSchema.enum.AdvancedHomeCare &&
    organizationRequiresInvoicingCodesBeforeDischarge(organization);

  const {
    data: finishedInvoicingCodes,
    isPending,
    isError,
  } = useQuery({
    ...finishedInvoicingCodesForPatientOptions({ patientId: patient.id }),
    enabled: shouldCheckForRequiredInvoicingCodes,
  });

  const isWaitingForFinishedInvoicingCodes =
    shouldCheckForRequiredInvoicingCodes && isPending;

  const requiredInvoicingCodes = new Set(
    EXPECTED_CODES_WHEN_DISCHARGING_AHC_PATIENT.map(({ code }) => code),
  );

  const missingRequiredInvoicingCodes = shouldCheckForRequiredInvoicingCodes
    ? // e.g. [1,2].difference([1]) -> [2]
      new Set(
        [...requiredInvoicingCodes].filter(
          (code) => !finishedInvoicingCodes?.includes(code),
        ),
      )
    : new Set([]);

  const { mutate: dischargePatientMutation, isPending: isDischargingPatient } =
    useMutation({
      mutationFn: ({
        patientId,
        dischargeDate,
        activitiesToClose,
      }: {
        patientId: string;
        dischargeDate: Date;
        activitiesToClose?: string[];
      }) => dischargePatient({ patientId, dischargeDate, activitiesToClose }),
      onError: (error) => {
        displayErrorMessageAlert(
          `${t`Det gick inte att skriva ut patienten.`} ${deducedError(error)}`,
        );
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries();
        void navigate("..", { relative: "path" });
      },
    });

  const methods = useForm<IDischargeFormData>({
    defaultValues: {
      cancelledScheduledActivities: false,
      dischargeDate: machineFormat(new Date(), "yyyy-MM-dd"),
      timeOfDischarge: "today",
      // One-time activities are left unchecked so the user opts in explicitly
      activityIds: activities
        .filter(({ recurring }) => recurring)
        .map(({ id }) => id),
    },
  });

  const {
    formState: { errors },
    handleSubmit,
    register,
    watch,
    resetField,
  } = methods;

  // oxlint-disable-next-line react/incompatible-library -- React Hook Form's watch API is not compiler-memoizable.
  const timeOfDischarge = watch("timeOfDischarge");

  useEffect(() => {
    if (timeOfDischarge === "today") {
      resetField("dischargeDate");
    }
  }, [timeOfDischarge, resetField]);

  const retroactiveDischargeDateSpan = {
    from: machineFormat(patient.admittedAt, "yyyy-MM-dd"),
    to: machineFormat(subDays(startOfToday(), 1), "yyyy-MM-dd"),
  };
  // A patient admitted today has no date before today to discharge on
  const canDischargeOnEarlierDate =
    retroactiveDischargeDateSpan.from <= retroactiveDischargeDateSpan.to;

  const [dischargeToReview, setDischargeToReview] =
    useState<IDischargeFormData | null>(null);

  const validateAndSubmit = handleSubmit((formData) => {
    setDischargeToReview(formData);
  });

  const missingInvoicingCodesNames =
    EXPECTED_CODES_WHEN_DISCHARGING_AHC_PATIENT.filter(({ code }) =>
      missingRequiredInvoicingCodes.has(code),
    ).map(({ name: nameOfMissingInvoicingCode }) => nameOfMissingInvoicingCode);

  return (
    <div className={styles.dischargeContent}>
      <FormProvider {...methods}>
        <Form onSubmit={validateAndSubmit}>
          <RadioButtons legend={t`Utskrivningsdatum`} orientation="horizontal">
            <RadioButton
              label={{ text: t`Idag` }}
              {...register("timeOfDischarge")}
              value={"today"}
              visualStyle="framed"
            />
            <RadioButton
              label={{ text: t`Tidigare datum` }}
              {...register("timeOfDischarge")}
              value={"earlier"}
              disabled={!canDischargeOnEarlierDate}
              visualStyle="framed"
            />
          </RadioButtons>
          {timeOfDischarge === "earlier" ? (
            <InputField
              label={t`Tidigare datum`}
              {...register("dischargeDate", {
                required: true,
                validate: (dischargeDate, { timeOfDischarge }) => {
                  if (timeOfDischarge === "today") {
                    return true;
                  }
                  if (dischargeDate > retroactiveDischargeDateSpan.to) {
                    return t`Välj ett datum före idag`;
                  }
                  if (dischargeDate < retroactiveDischargeDateSpan.from) {
                    return t`Utskrivningsdatumet kan inte vara före inskrivningsdatumet`;
                  }
                  return true;
                },
              })}
              type="date"
              errorMessage={errors.dischargeDate?.message}
              showOptionalLabel={false}
            />
          ) : null}

          {/* Activities to close */}
          {errorFetchingActivities ? (
            <div className={styles.errorAndConfirmation}>
              <ErrorMessage
                message={t`Kunde inte hämta information om ej avslutade aktiviteter. Du måste därför själv ansvara för att säkerställa att alla aktiviteter är avslutade.`}
              />
              <Checkbox
                errorMessage={errors.cancelledScheduledActivities?.message}
                label={{ text: t`Patientens aktiviteter är avslutade` }}
                {...register("cancelledScheduledActivities", {
                  required: {
                    value: true,
                    message: t`Bekräfta att aktiviteterna har avslutats`,
                  },
                })}
              />
            </div>
          ) : activities.length > 0 ? (
            <>
              <ActivitiesToCloseOrReactivate
                activities={activities}
                legend={t`Välj de aktiviteter som ska avslutas vid utskrivning`}
              />
              <Text element="p">
                <Trans>
                  Alla aktiviteter som avslutas här kan återskapas vid
                  återregistrering.
                </Trans>
              </Text>
            </>
          ) : null}
          {isWaitingForFinishedInvoicingCodes ? (
            <Loading
              message={t`Hämtar information om genomförda aktiviteter`}
            />
          ) : null}
          {isError ? (
            <ErrorMessage
              message={t`Kunde inte hämta information om genomförda aktiviteter. Du måste därför själv ansvara för att säkerställa att alla obligatoriska aktiviteter är genomförda.`}
            />
          ) : null}
          <div className={styles.actions}>
            <OutlinedButton asLinkTo=".." relative="path">
              {t`Tillbaka`}
            </OutlinedButton>
            <FilledButton
              disabled={isWaitingForFinishedInvoicingCodes}
              type="submit"
            >{t`Granska utskrivning`}</FilledButton>
          </div>
        </Form>
      </FormProvider>
      {dischargeToReview ? (
        <DischargeReview
          patient={patient}
          dischargeDate={dischargeToReview.dischargeDate}
          activitiesToCloseCount={dischargeToReview.activityIds.length}
          activitiesToKeepCount={
            activities.length - dischargeToReview.activityIds.length
          }
          missingInvoicingCodesNames={missingInvoicingCodesNames}
          disableSubmitButton={
            isDischargingPatient || isWaitingForFinishedInvoicingCodes
          }
          onCancel={() => {
            // Closing the review can't stop a discharge that is already sent
            if (isDischargingPatient) {
              return;
            }
            setDischargeToReview(null);
          }}
          onDischarge={() => {
            dischargePatientMutation({
              patientId: patient.id,
              dischargeDate: calendarDateSchema.decode(
                dischargeToReview.dischargeDate,
              ),
              activitiesToClose: dischargeToReview.activityIds,
            });
          }}
        />
      ) : null}
    </div>
  );
};
